/**
 * Route-Registry — 17 echte Apps-Script-Views + Hub (Phase 3.5 §5).
 *
 * Mapping zu `cotrainer-apps-script/app/*_view.html`:
 *   /                       → hub.ts (entspricht hub_view.html)
 *   /caller                 → caller_overview_view.html
 *   /caller/bilanz          → caller_bilanz_view.html
 *   /caller/pflege          → caller_pflege_view.html
 *   /meetings               → meetings_overview_view.html
 *   /meetings/kalender      → meetings_kalender_view.html
 *   /meetings/slots         → meetings_slots_view.html
 *   /fu                     → fu_overview_view.html
 *   /fu/kalender            → fu_kalender_view.html
 *   /funnel                 → funnel_status_view.html (default)
 *   /funnel/status          → funnel_status_view.html
 *   /funnel/performance     → funnel_performance_view.html
 *   /closings               → closings_overview_view.html
 *   /closings/liste         → closings_liste_view.html
 *   /pflegeluecken          → pflegeluecken_view.html
 *   /ziele                  → ziele_view.html
 *   /uebersicht             → vertrieb_uebersicht_view.html
 *   /dq                     → dq_view.html
 *
 * Stub-Routes liefern eine "Coming §5"-Karte mit Endpoint-Hint. Wenn eine
 * View portiert ist, ersetze ich den Eintrag durch echten Import.
 */

import { registerRoute } from "./router";

export function registerAllRoutes(): void {
  registerRoute("/", () => import("../views/hub"));
  registerRoute("/hub", () => import("../views/hub"));

  // Mein Bereich (Phase 3.x) — Profil, Konto, Urlaub (Platzhalter), Stunden (Platzhalter)
  registerRoute("/profil", () => import("../views/profil"));
  registerRoute("/mein/protokolle", () => import("../views/mein-protokolle")); // JV-9 — persönliche Protokoll-Entwürfe (nur P-019)
  registerRoute("/konto", () => import("../views/konto"));
  registerRoute("/urlaub", () => import("../views/urlaub"));
  registerRoute("/stunden", () => import("../views/stunden"));
  registerRoute("/krank", () => import("../views/krank"));
  // Baustein 6b — Host-Sperrzeiten pflegt der Host SELBST (Selbstbedienung).
  registerRoute("/sperrzeiten", () => import("../views/sperrzeiten"));

  // HR-Bereich (Phase 8) — nur HR-Leiter/Admin (Sichtbarkeit via bereiche.ts requiresBl)
  registerRoute("/hr", () => import("../views/hr-uebersicht"));
  registerRoute("/hr/urlaub", () => import("../views/hr-urlaub"));
  registerRoute("/hr/zeit", () => import("../views/hr-zeit"));
  registerRoute("/hr/einstellungen", () => import("../views/hr-einstellungen"));
  registerRoute("/hr/mitarbeiter", () => import("../views/hr-mitarbeiter"));
  registerRoute("/hr/mitarbeiter/:id", () => import("../views/hr-ma-detail"));
  registerRoute("/hr/abwesenheiten", () => import("../views/hr-abwesenheiten"));
  // 🆕 09.10.2026 — HR → Abgleich: fünf Reiter, eine Ansicht (sie liest den Reiter aus der Adresse).
  registerRoute("/hr/abgleich", () => import("../views/hr-abgleich"));
  registerRoute("/hr/abgleich/vorschau", () => import("../views/hr-abgleich"));
  registerRoute("/hr/abgleich/protokoll", () => import("../views/hr-abgleich"));
  registerRoute("/hr/abgleich/lohnlaeufe", () => import("../views/hr-abgleich"));
  registerRoute("/hr/abgleich/provision", () => import("../views/hr-abgleich"));
  registerRoute("/hr/abgleich/vorschlaege", () => import("../views/hr-abgleich"));

  // Kundenbetreuung (KB) — Vereinsbetreuung (K1c) + Einstellungen (P0b)
  registerRoute("/kb", () => import("../views/kb-vereine"));
  registerRoute("/kb/dashboard", () => import("../views/kb-dashboard"));
  registerRoute("/kb/sonderaktionen", () => import("../views/kb-sonderaktionen"));
  registerRoute("/kb/verein/:id", () => import("../views/kb-verein-detail"));
  registerRoute("/kb/kontakte", () => import("../views/kb-kontakte"));
  registerRoute("/kb/erfassung", () => import("../views/kb-erfassung"));
  registerRoute("/kb/betreuung", () => import("../views/kb-betreuungsliste")); // P8
  registerRoute("/kb/aufgaben", () => import("../views/kb-aufgaben")); // Paket 3
  registerRoute("/kb/kalender", () => import("../views/kb-kalender")); // P11
  registerRoute("/kb/bestand", () => import("../views/kb-bestand")); // P13c
  registerRoute("/kb/einstellungen", () => import("../views/kb-einstellungen"));
  // Phase 10b: Vorstandseinrichtung + Trainereinführung buchen (V6b).
  // Bewusst NICHT unter /kb allein gedacht: gebucht wird auch vom VERTRIEB
  // im Closing (E-9). Die Route liegt hier, weil die KB sie fachlich trägt.
  // ═══ TERMINSTRECKE v2 (30.08.) ═══════════════════════════════════════════
  // 🔑 Zehn Schirme, zehn Routen — die abgenommene optische Spezifikation.
  // Der alte Ein-View-mit-sechs-Reitern bleibt vorerst unter /kb/termine/alt
  // erreichbar: die Funktionen darin sind erprobt, und ein Umbau, der den
  // Rückweg abschneidet, ist keiner.
  /**
   * 🆕 A6 Phase 2 · A-2 (05.10.2026) — die Vereinseinrichtung im VERTRIEB.
   * Dieselben Module wie unter `/kb/termine/*`; sie erkennen den Ort an der
   * Adresse (`views/ve/ve-ort.ts`) und stehen dann fest auf VE.
   * ⚠️ Eigene Adressen statt Verweisen auf `/kb/termine`: `findViewForRoute`
   * nimmt den ERSTEN Treffer, und der Vertrieb steht vor der KB — ein Vertriebs-
   * Eintrag auf `/kb/termine/…` hätte der KB ihre Seitenleiste genommen.
   */
  registerRoute("/vertrieb/ve", () => import("../views/ve/ve-dashboard"));
  registerRoute("/vertrieb/ve/kalender", () => import("../views/ve/ve-kalender"));
  registerRoute("/vertrieb/ve/buchen", () => import("../views/ve/ve-buchen"));
  registerRoute("/vertrieb/ve/zeiten", () => import("../views/ve/ve-meine-zeiten"));
  registerRoute("/vertrieb/ve/zuteilung", () => import("../views/ve/ve-zuteilung"));
  // ⚠️ Zuletzt: die Parameter-Route würde sonst /vertrieb/ve/buchen abfangen.
  registerRoute("/vertrieb/ve/:id", () => import("../views/ve/ve-detail"));

  registerRoute("/kb/termine", () => import("../views/ve/ve-dashboard"));
  // 🔴 31.08.2026 — der Schirm ist weg, die ADRESSE bleibt. Sie stand in Slack,
  // in Lesezeichen und in aelteren Uebergaben. 🔑 *Eine Adresse, die einmal
  // funktioniert hat, darf nicht ins Leere fallen — sie fuehrt weiter, wohin ihr
  // Inhalt gezogen ist.* Sonst meldet der Detailschirm „Diesen Termin gibt es
  // nicht“, genau wie am 31.08. bei `/kb/termine/alt`.
  registerRoute("/kb/termine/zu-tun", () => Promise.resolve({
    render: () => { void import("./router").then((m) => m.navigate("/kb/termine/kalender")); }
  }));
  // 🔴 Der ACHTE Reiter — bis zum 31.08. fehlte er, weil beim Bau stand,
  // `klaerfaelle.html` gebe es im Design-Projekt nicht. Es gibt sie, 402 Zeilen.
  registerRoute("/kb/termine/klaerfaelle", () => import("../views/ve/ve-klaerliste"));
  registerRoute("/kb/termine/kalender", () => import("../views/ve/ve-kalender"));
  registerRoute("/kb/termine/buchen", () => import("../views/ve/ve-buchen"));
  registerRoute("/kb/termine/versandbuch", () => import("../views/ve/ve-versandbuch"));
  registerRoute("/kb/termine/mailtexte", () => import("../views/ve/ve-mailtexte"));
  registerRoute("/kb/termine/ergebnisse", () => import("../views/ve/ve-ergebnisse"));
  registerRoute("/kb/termine/bestand", () => import("../views/ve/ve-bestand"));
  // 🆕 17.09.2026 — zwei Reiter, EIN Schirm (Justin: „wir brauchen einen Reiter
  // Vereine ohne Vereinseinrichtung und Vereine ohne Trainerfortbildung").
  // 🔑 Zwei Module, die sich nur in einem Wort unterscheiden, waeren zwei Orte
  // fuer eine Sache — deshalb ein Modul mit zwei Ausgaengen.
  registerRoute("/kb/termine/ohne-ve", async () =>
    ({ render: (await import("../views/ve/ve-ohne-termin")).renderOhneVe }));
  registerRoute("/kb/termine/ohne-tf", async () =>
    ({ render: (await import("../views/ve/ve-ohne-termin")).renderOhneTf }));
  // 🔴 **Der Rückweg lag HINTER der Parameter-Route** — bis 31.08.2026.
  // `/kb/termine/alt` fiel damit auf `/kb/termine/:id` durch und meldete
  // „Diesen Termin gibt es nicht. Die Kennung `alt` führt zu keiner Zeile.“
  // ⚠️ Die Warnung eine Zeile weiter unten benennt genau diese Regel — und die
  // Zeile darunter hat sie gebrochen. *Ein Kommentar, der die Regel kennt,
  // ersetzt nicht die Prüfung, ob sie eingehalten wird.*
  // 🔑 Die Folge war teuer: an diesem Schirm hängen die sieben Pflege-
  // funktionen, darunter `veFensterSave` — **die Hosts konnten ihre Zeiten
  // nicht mehr eintragen.** Genau das erklärt die null TF-Fenster.
  registerRoute("/kb/termine/alt", () => import("../views/ve-termine"));
  // Derselbe Schirm, aber auf dem Reiter „Meine Zeiten“ — die Adresse, die in der
  // Sidebar steht. Begruendung im Kopf von `views/ve-termine.ts`.
  /**
   * 🆕 01.09. — eigener Schirm statt Reiter auf dem alten Sammelschirm.
   * 📏 Der Entwurf lag seit BRIEF-6 (31.08.) im Design-Projekt und war nie ins
   * Repo geholt worden; deshalb zeigte diese Adresse zwei Monate lang auf
   * `views/ve-termine`, waehrend die Sidebar sie wie einen neuen Schirm fuehrte.
   */
  registerRoute("/kb/termine/zeiten", () => import("../views/ve/ve-meine-zeiten"));
  /**
   * 🆕 02.09. (B5) — der Schirm der Bereichsleitung. Die Maske gab es funktional
   * schon auf `/kb/termine/alt`; neu ist, dass Zuteilung und WIRKUNG in
   * derselben Zeile stehen.
   */
  registerRoute("/kb/termine/zuteilung", () => import("../views/ve/ve-zuteilung"));
  // ⚠️ Zuletzt: die Parameter-Route wuerde sonst /kb/termine/buchen abfangen.
  registerRoute("/kb/termine/:id", () => import("../views/ve/ve-detail"));
  // E10: Nachfolgeregelung — dieselbe View wie im Vertrieb, zeigt nur die Rollen,
  // für die der Nutzer Bereichsleitung ist.
  registerRoute("/kb/nachfolge", () => import("../views/nachfolge"));
  registerRoute("/kb/uebergabe", () => import("../views/uebergabe"));
  registerRoute("/kb/audit", () => import("../views/kb-audit"));

  // Finance (F0) — Fundament: Übersicht (Landing) + geteilte Taxonomie.
  registerRoute("/finance", () => import("../views/finance-uebersicht"));
  registerRoute("/finance/taxonomie", () => import("../views/finance-taxonomie"));
  // Finance (F1) — Finance Master in der App: Vereinsabo-Liste + Detail.
  // 🆕 02.11.2026 — SEPA-Papiermandate (SP1 Liste, SP2 Freigabe; Entwurf Mystique).
  registerRoute("/finance/sepa-papier", () => import("../views/finance-sepa-papier"));
  registerRoute("/finance/sepa-papier/:scanId", () => import("../views/finance-sepa-papier-freigabe"));
  registerRoute("/finance/vereinsabo", () => import("../views/finance-vereinsabo"));
  // Redesign: "Ausstehende Posten" — MUSS vor :id stehen (sonst fängt :id "offen").
  registerRoute("/finance/vereinsabo/offen", () => import("../views/finance-vereinsabo-offen"));
  // B20: "Geplante Abos" (subscription_schedules) — ebenfalls VOR :id.
  registerRoute("/finance/vereinsabo/geplant", () => import("../views/finance-vereinsabo-geplant"));
  registerRoute("/finance/vereinsabo/:id", () => import("../views/finance-vereinsabo-detail"));
  // Finance (F8) — Stripe→HubSpot Live-Preis-Sync (Dry-Run-Report + Gating).
  registerRoute("/finance/preis-sync", () => import("../views/finance-preis-sync"));
  // Finance (F2) — IST-Layer / Buchhaltung: Konten + kategorisiertes Kontobuch.
  registerRoute("/finance/konten", () => import("../views/finance-konten"));
  registerRoute("/finance/buchhaltung", () => import("../views/finance-buchhaltung"));
  // Redesign: Schnellerfassung (neue View).
  registerRoute("/finance/erfassung", () => import("../views/finance-erfassung"));
  // Cash-Sicht: Bank-CSV-Import (Sparkasse CAMT) — erreichbar per Button unter Konten.
  // Die Kategorisierung ist als Tab im Kontobuch integriert.
  registerRoute("/finance/import", () => import("../views/finance-import"));
  // Finance (F3) — Business Case SOLL/PLAN + IST: Annahmen, Plan-Output, Soll-Ist.
  registerRoute("/finance/plan", () => import("../views/finance-plan-output"));
  registerRoute("/finance/plan/annahmen", () => import("../views/finance-plan-annahmen"));
  registerRoute("/finance/soll-ist", () => import("../views/finance-soll-ist"));
  // Stufe 1b — W1-FI-10 Monatsabschluss (ein geschlossener Monat ist fest) +
  // W1-IN-2 Investoren-Archiv (was ausgestellt wurde, mit damaliger Definition).
  registerRoute("/finance/monatsabschluss", () => import("../views/finance-monatsabschluss"));
  registerRoute("/finance/investor-archiv", () => import("../views/finance-investor-archiv"));
  // Investoren-Automatik — Kennzahlen je Zeitraum, aufklappbar bis auf die Vereine.
  registerRoute("/finance/investor-update", () => import("../views/finance-investor-update"));
  // Finance (F4) — Ziele + Reporting / Gesellschafter-One-Pager.
  registerRoute("/finance/ziele", () => import("../views/finance-ziele"));
  registerRoute("/finance/reporting", () => import("../views/finance-reporting"));
  // Redesign: One-Pager-Zusammenfassung (gleiche View wie Reporting).
  registerRoute("/finance/zusammenfassung", () => import("../views/finance-reporting"));
  // Finance (F7) — Investor-Stufe: MRR-Movement-Wasserfall + Cohort-Retention.
  registerRoute("/finance/mrr", () => import("../views/finance-mrr"));
  // Finance (F6) — Ausbau-Controlling: Margen/Personal/DSO/Reforecast/Kostenstellen.
  // Redesign: /finance/dashboard → ins Cockpit gefaltet (Operativ/Exec-Toggle); /finance/ops entfällt.
  registerRoute("/finance/dashboard", () => import("../views/finance-uebersicht"));
  registerRoute("/finance/margen", () => import("../views/finance-margen"));
  registerRoute("/finance/personal", () => import("../views/finance-personal"));
  registerRoute("/finance/forderungen", () => import("../views/finance-forderungen"));
  registerRoute("/finance/reforecast", () => import("../views/finance-reforecast"));
  registerRoute("/finance/kostenstellen", () => import("../views/finance-kostenstellen"));
  // Finance — Einstellungen (Mahn-Vorlage, R7-Rekonstruktion).
  registerRoute("/finance/einstellungen", () => import("../views/finance-einstellungen"));

  // App (E9/P4) — Store-Kennzahlen aus Google Play + App Store Connect.
  // Lesen darf jeder Mitarbeiter; die Pflege-Views sind backend-seitig auf
  // Admin gegated (und in bereiche.ts als adminOnly markiert).
  registerRoute("/app", () => import("../views/app-uebersicht"));
  registerRoute("/app/bewertungen", () => import("../views/app-bewertungen"));
  registerRoute("/app/kennzahlen", () => import("../views/app-kennzahlen"));
  registerRoute("/app/import", () => import("../views/app-import"));
  registerRoute("/app/einstellungen", () => import("../views/app-einstellungen"));

  // Admin (Phase 3.7.2) — MA-Verwaltung
  registerRoute("/admin/ma", () => import("../views/admin-ma"));
  // Admin (Feature B) — Berechtigungs-Matrix (read-only Übersicht, Edit via MA-Modal)
  registerRoute("/admin/berechtigungen", () => import("../views/admin-berechtigungen"));
  registerRoute("/admin/zugriffsanfragen", () => import("../views/admin-zugriffsanfragen"));
  // Admin (Phase 3.7.4) — System-Settings
  registerRoute("/admin/settings", () => import("../views/admin-settings"));
  // Admin (Phase 3.7.5) — Sheet-Inventar
  registerRoute("/admin/sheets", () => import("../views/admin-sheets"));
  // Admin (Phase 3.7.6) — Audit-Log
  registerRoute("/admin/audit", () => import("../views/admin-audit"));
  // Admin (Phase 7.4) — System: Datumsformat
  registerRoute("/admin/system/datumsformat", () => import("../views/admin-system-format"));
  registerRoute("/admin/system/feiertage", () => import("../views/admin-feiertage"));
  registerRoute("/admin/system/companies-cache", () => import("../views/admin-companies-cache"));
  registerRoute("/vertrieb/einstellungen/arena-regeln", () => import("../views/vertrieb-arena-regeln")); // Arena: Regelwerk ohne Deploy pflegen
  registerRoute("/admin/system/integrationen", () => import("../views/admin-integrations"));
  // Admin (E12b-2) — Automatik-Sicht: jede Automatik, ihre Läufe und ihre Skips.
  registerRoute("/admin/system/automatiken", () => import("../views/admin-automationen"));
  // Admin (R7 WP5) — Gate-Cockpit (read-only Ist-Zustand aller Feature-Gates)
  registerRoute("/admin/gates", () => import("../views/admin-gates"));
  // Vertrieb → Einstellungen (Phase 7.4 + Nav-Umbau 2026-06-04):
  // Container-View mit Tab-Leiste (Zahnrad im Sub-Sidebar-Head). Die nackten
  // Einzel-View-Routen laufen jetzt alle über den Container, der die passende
  // Sub-View einbettet. /vertrieb/einstellungen (ohne Sub) → erster Tab.
  const vertriebEinstellungen = () => import("../views/vertrieb-einstellungen");
  registerRoute("/vertrieb/einstellungen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/roster", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/slot-verfuegbarkeit", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/slot-rotation", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/schwellen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/slack-templates", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/referenzen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/lost-grund", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/nachfolge", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/uebergabe", vertriebEinstellungen);

  // -----------------------------------------------------------------------
  // BATCH A — Worker-Endpoints aus Phase 3.2 vorhanden
  // -----------------------------------------------------------------------
  registerRoute("/pflegeluecken", () => import("../views/pflegeluecken"));
  registerRoute("/fu", () => import("../views/fu-overview"));
  registerRoute("/fu/kalender", () => import("../views/fu-kalender"));

  // -----------------------------------------------------------------------
  // BATCH B — Worker-Endpoints fehlen, kommen mit §5
  // -----------------------------------------------------------------------
  registerRoute("/caller", () => import("../views/caller-overview"));
  registerRoute("/caller/bilanz", () => import("../views/caller-bilanz"));
  registerRoute("/meetings", () => import("../views/meetings-overview"));
  registerRoute("/meetings/kalender", () => import("../views/meetings-kalender"));
  // Baustein 4 (E12b-7): Caller-Buchung + Inbound-Anfragen.
  registerRoute("/meetings/buchen", () => import("../views/buchen"));
  registerRoute("/meetings/anfragen", () => import("../views/anfragen"));
  registerRoute("/closings", () => import("../views/closings-overview"));
  registerRoute("/closings/liste", () => import("../views/closings-liste"));
  // B3 (26.08.): der Einstieg des Vertriebs. Eigene Seite, KEIN Dialog — das
  // Closing entsteht automatisch aus der Bestellabwicklung, es gibt also keinen
  // Moment, in dem jemand einen Closing-Dialog öffnet.
  // 🔑 EINE Route, ZWEI Reiter (Justin, 30.08.). Buchen steht vorn: es ist der
  // Griff im laufenden Telefonat, die Liste das Nacharbeiten danach.
  registerRoute("/closings/terminierung", () => import("../views/closings-terminierung-tabs"));
  // Widerruf-Register. DREI Routen auf DIESELBE View — mit Absicht:
  // 🔑 Der Vorgang gehört Vertrieb, Kundenbetreuung und Finance gemeinsam, und
  // `findViewForRoute` nimmt bei mehrfach registrierter Route immer den ERSTEN
  // Bereich. Ein KB-Nutzer bekäme dann die Vertriebs-Sub-Sidebar zu sehen.
  // Drei eigene Pfade lösen das ohne Sonderfall in der Navigation.
  //
  // 🔑 NEUBAU 30.08.: drei Schirme je Einstieg (Übersicht · Melden · Fall).
  // Die Adresse trägt die ROLLE — `w-rahmen.ts` liest aus ihr, aus wessen
  // Sicht gearbeitet wird. Deshalb bekommt jeder der drei Einstiege dieselben
  // Unterwege; eine gemeinsame Detailroute hätte die Sicht verloren.
  for (const b of ["/widerrufe", "/kb/widerrufe", "/finance/widerrufe"]) {
    registerRoute(b, () => import("../views/widerruf/widerruf-uebersicht"));
    registerRoute(`${b}/melden`, () => import("../views/widerruf/widerruf-melden"));
    // Mit Vorauswahl aus ② der Übersicht („Widerruf melden" an einer Zeile).
    registerRoute(`${b}/melden/:companyId`, () => import("../views/widerruf/widerruf-melden"));
    registerRoute(`${b}/fall/:fallId`, () => import("../views/widerruf/widerruf-detail"));
    // §12 (Justin, 10.09.2026): die Widerrufsquote je Closer. Sie hängt an
    // denselben drei Einstiegen wie der Rest — 🔑 die Frage „welcher Closer hat
    // die höchste Quote" stellt sich dort, wo man die Widerrufe ansieht, nicht
    // in einer eigenen Auswertungs-Ecke.
    registerRoute(`${b}/quote`, () => import("../views/widerruf/widerruf-quote"));
    // Reiter derselben Übersicht (Justin, 11.09.2026). 🔑 Sie laden DIESELBE
    // View — der Reiter entscheidet nur, was von den einmal geladenen Daten
    // gezeigt wird. *Fünf Reiter mit fünf Ladezuständen wären fünf
    // Gelegenheiten, einen davon zu vergessen.*
    // 🆕 18.09.2026 — der Schnitt „Bei mir" / „Weiterhin offen" (Justin, 17.09.).
    // `${b}` selbst ist „Bei mir"; dies hier ist die Gegenliste.
    // 🆕 18.09.2026 — das Dashboard. Eigene View, eigener Endpunkt.
    registerRoute(`${b}/dashboard`, () => import("../views/widerruf/widerruf-dashboard"));
    registerRoute(`${b}/offen`, () => import("../views/widerruf/widerruf-uebersicht"));
    registerRoute(`${b}/abgeschlossen`, () => import("../views/widerruf/widerruf-uebersicht"));
    registerRoute(`${b}/rechte`, () => import("../views/widerruf/widerruf-uebersicht"));
    registerRoute(`${b}/luecke`, () => import("../views/widerruf/widerruf-uebersicht"));
    registerRoute(`${b}/abgleich`, () => import("../views/widerruf/widerruf-uebersicht"));
    // 22.09.2026 — „Absprachen pruefen" ist ein eigener Reiter der Gruppe
    // „Pruefung" (vorher ein eingeklappter Block unter den Vorgaengen).
    registerRoute(`${b}/absprachen`, () => import("../views/widerruf/widerruf-uebersicht"));
    // 🆕 01.10.2026 — die Zeitraum-Liste, deckungsgleich zur Closingliste.
    // 🔑 EIGENE View und eigener Ladevorgang: sie beantwortet eine andere Frage
    // als ihre Nachbarn (was war in einem ZEITRAUM, nicht was liegt an) und
    // holt ihre Daten mit einem Zeitraum, den die Übersicht nicht kennt.
    registerRoute(`${b}/liste`, () => import("../views/widerruf/widerruf-liste"));
  }
  // 🔴 Der alte Ein-Schirm-Aufbau bleibt als Rückweg erreichbar, bis der Neubau
  // im Betrieb bestätigt ist. Ein Umbau, der den Rückweg abschneidet, ist keiner.
  registerRoute("/widerrufe/alt", () => import("../views/widerrufe"));
  // Kündigungsregister (Punkt 18, 21.09.2026). Fünf Reiter auf DIESELBE View —
  // sie zeigen alle dieselben einmal geladenen Daten, der Reiter entscheidet nur
  // die Sicht. 🔑 *Fünf Reiter mit fünf Ladezuständen wären fünf Gelegenheiten,
  // einen davon zu vergessen* — dieselbe Begründung wie beim Widerrufsregister.
  //
  // 🔄 21.09.2026 — DREI Einstiege statt einem (Justin: „Kündigungsregister soll
  // eigener Sidebar-Punkt werden wie widerrufe"). Derselbe Grund wie dort:
  // `findViewForRoute` nimmt bei mehrfach registrierter Route immer den ERSTEN
  // Bereich, ein KB-Nutzer bekäme sonst die Vertriebs-Sub-Sidebar.
  for (const b of ["/kuendigungen", "/kb/kuendigungen", "/finance/kuendigungen"]) {
    // 🔄 26.09.2026 — `/offen` dazu: „Laufend" ist in **Bei mir** und
    // **Weiterhin offen** geschnitten, 1:1 wie beim Widerruf.
    // ⚠️ Der Reiter wechselt per `pushState` — ohne registrierte Route liefe
    // ein Neuladen dieser Adresse ins Leere. *Ein Reiter, der nur beim Klicken
    // funktioniert, bricht beim Lesezeichen.*
    for (const p of ["", "/offen", "/anwaerter", "/unstimmig", "/abweichung", "/absprachen", "/archiv"]) {
      registerRoute(`${b}${p}`, () => import("../views/kuendigung/kuendigung-uebersicht"));
    }
    // 🆕 28.09.2026 — das Dashboard, wie beim Widerruf. Eigener Schirm, eigener
    // Ladevorgang: 🔑 *ein Reiter, der eine andere Frage beantwortet als seine
    // Nachbarn, ist keiner* — hier stehen Verläufe, dort Arbeitslisten.
    registerRoute(`${b}/dashboard`, () => import("../views/kuendigung/kuendigung-dashboard"));
    // 🆕 01.10.2026 — die Zeitraum-Liste, deckungsgleich zur Closingliste.
    registerRoute(`${b}/liste`, () => import("../views/kuendigung/kuendigung-liste"));
    // Die Fall-Ansicht (21.09.2026) — das Herzstück der Bedienung. Sie hängt an
    // derselben Basis, damit man dort bleibt, wo man hergekommen ist.
    registerRoute(`${b}/fall/:id`, () => import("../views/kuendigung/kuendigung-detail"));
    /**
     * 🆕 28.09.2026 — **die Vertragsregel je VEREIN, ohne Vorgang.**
     *
     * 📏 Die Bedienform war seit dem 26.09. gebaut und hing am Falldetail. Am
     * 28.09. gemessen: **zwei** Vorgänge im Register, und **keiner** der sechs
     * Vereine, die eine Regel brauchten, hatte einen.
     * 🔑 ***Eine Bedienform am Vorgang erreicht nur Vereine, bei denen schon
     * gekündigt wurde — aber eine Regel legt man fest, BEVOR gekündigt wird.***
     * ⚠️ Schlüssel ist die `company_id`, nicht die Fall-Nummer: der Verein
     * existiert auch ohne Kündigung, der Fall nicht.
     */
    registerRoute(`${b}/regel/:companyId`, () => import("../views/kuendigung/kuendigung-regel"));
    /**
     * 🆕 28.09.2026 — die Auswertung, auf Justins *„Ja, 1:1 wie beim Widerruf"*.
     * Derselbe Pfad wie dort (`/quote`), damit niemand zwei Namen für dieselbe
     * Sache lernen muss.
     */
    registerRoute(`${b}/quote`, () => import("../views/kuendigung/kuendigung-quote"));
    /**
     * 🆕 28.09.2026 — die Meldemaske, auf Justins „kündigung muss auch darüber
     * gemeldet werden können". Zwei Wege wie beim Widerruf: mit und ohne
     * vorgewählten Verein.
     */
    registerRoute(`${b}/melden`, () => import("../views/kuendigung/kuendigung-melden"));
    registerRoute(`${b}/melden/:companyId`, () => import("../views/kuendigung/kuendigung-melden"));
  }
  /**
   * 🆕 04.10.2026 — VERTRAGSANPASSUNGEN (Deckblatt-Bau § 4.8): das Register der Abreden,
   * in denselben drei Bereichen wie Kündigung und Widerruf — derselbe Grund wie dort
   * (`findViewForRoute` nimmt den ERSTEN Bereich einer mehrfach registrierten Route).
   */
  for (const b of ["/vertragsanpassungen", "/kb/vertragsanpassungen", "/finance/vertragsanpassungen"]) {
    registerRoute(b, () => import("../views/vertragsanpassung/va-liste"));
    registerRoute(`${b}/alle`, () => import("../views/vertragsanpassung/va-liste"));
    registerRoute(`${b}/erfassen`, () => import("../views/vertragsanpassung/va-erfassen"));
    registerRoute(`${b}/altvertrag`, () => import("../views/vertragsanpassung/va-altvertrag"));
    registerRoute(`${b}/nachzug`, () => import("../views/vertragsanpassung/va-nachzug"));
  }
  registerRoute("/funnel", () => import("../views/funnel-status"));
  registerRoute("/funnel/status", () => import("../views/funnel-status"));
  registerRoute("/funnel/performance", () => import("../views/funnel-performance"));  registerRoute("/uebersicht", () => import("../views/vertriebs-uebersicht"));
  registerRoute("/vertriebshistorie", () => import("../views/vertriebshistorie"));
  registerRoute("/vertriebshistorie/:companyId", () => import("../views/vertriebshistorie-detail"));
  registerRoute("/ziele", () => import("../views/ziele"));
  registerRoute("/vertrieb/liga", () => import("../views/vertrieb-liga")); // Paket 4 — Gamification (BL-only bis Go-live)
  // ═══ LEAD-AGENT — EIN BEHAELTER, ZWEI WERKZEUGE (17.09.2026) ═════════════
  // Justin: "unter einem Sidebarpunkt zusammengefasst ... die Unterpunkte
  // davon dann jeweils als Tabs."
  // 🔴 ALLE bisherigen Adressen bleiben gueltig — sie rendern jetzt denselben
  // Behaelter, der die Tab-Leiste seines Werkzeugs davorsetzt und die
  // vorhandene Ansicht darunter laedt (dynamisch, damit die Buendel getrennt
  // bleiben). 🔑 Eine Umsortierung, die Adressen aendert, bricht Lesezeichen
  // — und zwar still.
  const leadagentBehaelter = () => import("../views/vertrieb-leadagent-werkzeug");
  registerRoute("/vertrieb/leadagent/neu", leadagentBehaelter);        // Werkzeug ①
  registerRoute("/vertrieb/leadagent/pflege", leadagentBehaelter);     // Werkzeug ②
  registerRoute("/vertrieb/leadagent/lage", leadagentBehaelter);       // ueber beide
  registerRoute("/vertrieb/leadagent", leadagentBehaelter); // Herkunft je Angabe, Widerspruch nebeneinander
  registerRoute("/vertrieb/leadagent/start", leadagentBehaelter); // v2 — Suche zuschneiden, Kostenschaetzung, harte Kante
  registerRoute("/vertrieb/leadagent/jobs", leadagentBehaelter); // v3 — Jobs, Fortschritt, Warteschlange
  registerRoute("/vertrieb/leadagent/luecken", leadagentBehaelter); // Stufe 2 — Websuche nur fuer die unvollstaendigen Leads
  registerRoute("/vertrieb/leadagent/uebersicht", leadagentBehaelter); // v1 — drei Zahlen mit Nenner, Auffaelligkeiten als LISTE
  registerRoute("/vertrieb/leadagent/protokoll", leadagentBehaelter); // v4 — leer und blockiert getrennt
  registerRoute("/vertrieb/leadagent/handpruefung", leadagentBehaelter); // v7 — Fundort ist Pflicht
  // 🔴 Das Vereinsdetail gehoert zu KEINEM Werkzeug — der Behaelter erkennt das
  //    und zeigt es ohne erfundene Tab-Leiste.
  registerRoute("/vertrieb/leadagent/verein", () => import("../views/vertrieb-leadagent-verein")); // v6 — Vereinsdetail, eigener Schirm wie im Entwurf
  registerRoute("/vertrieb/leadagent/leitung", leadagentBehaelter); // v8 — gezaehlte Tatsachen, keine Guete-Zahl
  registerRoute("/vertrieb/leadagent/landkarte", leadagentBehaelter); // v10 — Abdeckung je Land, drei Zahlen NEBENeinander
  registerRoute("/vertrieb/leadagent/qualitaet", leadagentBehaelter); // v11 — Sollliste in der Ansicht, damit die Nullen sichtbar sind
  registerRoute("/vertrieb/leadagent/korrekturen", leadagentBehaelter); // v13 — Abgleich CRM->Quelle, ein Fehlschlag heisst hier nichts
  registerRoute("/vertrieb/leadagent/lehren", leadagentBehaelter); // Phase 4 — drei Koerbe, und kein Knopf "uebernehmen" im zweiten
  registerRoute("/vertrieb/leadagent/sperrliste", leadagentBehaelter); // 17.09. — Vereine, die wir NICHT wollen, und warum
  registerRoute("/vertrieb/arena", () => import("../views/vertrieb-arena")); // Ausbaustufe 4 — Arena (BL-only bis ARENA_LIVE)
  // Partnervereine: Liste + Umkreissuche + Karte. Eigene Oberkategorie im
  // Vertrieb (Justin 17.08.: „wichtig ist aber, dass das im vertrieb liegen muss
  // und nicht in kb"). /karte MUSS vor der Liste keine Sonderstellung haben —
  // beide sind feste Pfade, kein :id-Parameter fängt hier etwas ab.
  registerRoute("/vertrieb/partnervereine", () => import("../views/vertrieb-partnervereine"));
  registerRoute("/vertrieb/partnervereine/karte", () => import("../views/vertrieb-partnervereine-karte"));
  registerRoute("/dq", () => import("../views/dq"));

  // -----------------------------------------------------------------------
  // BATCH C — Write-Dialoge in Views (kein eigener Route)
  // -----------------------------------------------------------------------
  registerRoute("/meetings/slots", () => import("../views/meetings-slots"));
  // E11 + Baustein 6b: Termine, deren Host abwesend oder gesperrt ist.
  // Seit 18.09.2026 vier Orte statt einer Seite mit fünf Blöcken — die
  // Gruppenadresse bleibt das Dashboard, damit alte Verweise nicht brechen.
  registerRoute("/meetings/vertretung", () => import("../views/vertretung"));
  registerRoute("/meetings/vertretung/klaerung", () => import("../views/vertretung-klaerung"));
  registerRoute("/meetings/vertretung/doppelbelegungen", () => import("../views/vertretung-doppel"));
  registerRoute("/meetings/vertretung/regeln", () => import("../views/vertretung-regeln"));
  registerRoute("/caller/pflege", () => import("../views/caller-pflege"));
}

